import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { type Place, placeMenu, replaceMenu } from "./menuPlace.ts";

const ITEMS = "[role^=menuitem]:not(:disabled)";

/**
 * A button with a popup menu: arrow keys move over the enabled items, Escape closes, a press outside closes.
 * Inside a list row (a tree row, say) the popup keeps its clicks, presses and keys to itself, so nothing
 * reaches the row. `open`/`onOpenChange` let a parent open it (a right-click on the row); `fixed` places the
 * popup against the viewport so a scrolling sidebar cannot clip it.
 */
export function Menu({
  label,
  button,
  children,
  align = "start",
  up = false,
  buttonClass = "top-btn",
  open: openProp,
  onOpenChange,
  fixed = false,
  buttonTabIndex,
  menuClass,
}: {
  label: string;
  button: ReactNode;
  children: (close: () => void) => ReactNode;
  align?: "start" | "end";
  /** open above the button (the account row sits at the bottom of the sidebar) */
  up?: boolean;
  buttonClass?: string;
  /** controlled mode: whether the menu is open */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** position the popup against the viewport, from the button's rectangle */
  fixed?: boolean;
  buttonTabIndex?: number;
  /** an extra class on the popup (the phone's account menu anchors itself inside the viewport) */
  menuClass?: string;
}) {
  const [inner, setInner] = useState(false);
  const open = openProp ?? inner;
  const setOpen = useCallback(
    (v: boolean) => {
      if (openProp === undefined) setInner(v);
      onOpenChange?.(v);
    },
    [openProp, onOpenChange],
  );
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<Place | null>(null);
  /** Opened with a pointer: focus waits on the popup itself, so no item looks chosen until the keyboard moves. */
  const byPointer = useRef(false);

  /** Close and hand focus back: to the tree row the menu belongs to, or to its own button. */
  const closeAndFocus = () => {
    setOpen(false);
    (btn.current?.closest<HTMLElement>("[role=treeitem]") ?? btn.current)?.focus();
  };

  // Fixed popups are placed before the first paint, so they never flash at the wrong spot.
  useLayoutEffect(() => {
    if (!open || !fixed || !btn.current) {
      setPlace(null);
      return;
    }
    setPlace(placeMenu(btn.current.getBoundingClientRect(), window.innerWidth, window.innerHeight));
  }, [open, fixed]);

  // The popup mounts once `place` is set. Measure its untransformed width before paint, then keep both
  // edges inside the viewport (the pop animation can temporarily change getBoundingClientRect()).
  useLayoutEffect(() => {
    if (!open || !fixed || !place || !popup.current) return;
    const right = Math.max(8, Math.min(place.right, window.innerWidth - popup.current.offsetWidth - 8));
    if (right !== place.right) setPlace({ ...place, right });
  }, [open, fixed, place]);

  useEffect(() => {
    if (!open) return;
    // Capture phase: a press inside another menu's popup (which stops its own events) still closes this one.
    const h = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener("mousedown", h, true);
    // A fixed popup follows its button when the page scrolls or resizes under it (scrolling inside the popup is
    // its own business), and closes only once the button has left the viewport.
    const away = (e: Event) => {
      if (e.type === "scroll" && wrap.current?.contains(e.target as Node)) return;
      if (!btn.current) return;
      const next = replaceMenu(btn.current.getBoundingClientRect(), window.innerWidth, window.innerHeight);
      if (next) setPlace(next);
      else setOpen(false);
    };
    if (fixed) {
      window.addEventListener("scroll", away, true);
      window.addEventListener("resize", away);
    }
    return () => {
      window.removeEventListener("mousedown", h, true);
      window.removeEventListener("scroll", away, true);
      window.removeEventListener("resize", away);
    };
  }, [open, fixed, setOpen]);

  // Focus goes to the checked item, or the first enabled one, each time the menu opens from the keyboard (or a
  // parent). Opened with a pointer it goes to the popup: arrow keys then start at the first item.
  // Only once per opening: a fixed popup is placed again when the page scrolls, which must not move focus back.
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (!open) {
      focusedOnce.current = false;
      return;
    }
    if ((fixed && !place) || focusedOnce.current) return;
    focusedOnce.current = true;
    if (byPointer.current) {
      byPointer.current = false;
      popup.current?.focus({ preventScroll: true });
      return;
    }
    (
      wrap.current?.querySelector<HTMLElement>("[aria-checked=true]") ??
      wrap.current?.querySelector<HTMLElement>(ITEMS)
    )?.focus({ preventScroll: true });
  }, [open, fixed, place]);

  return (
    <div className="menu-wrap" ref={wrap}>
      <button
        ref={btn}
        type="button"
        className={buttonClass}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        tabIndex={buttonTabIndex}
        onPointerDown={() => {
          byPointer.current = !open;
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        onDoubleClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          byPointer.current = false;
          if (!((e.metaKey || e.ctrlKey) && e.key.length === 1)) e.stopPropagation();
        }}
      >
        {button}
      </button>
      {open && (!fixed || place) && (
        <div
          ref={popup}
          className={`menu ${align}${up ? " up" : ""}${fixed ? " fixed" : ""}${menuClass ? ` ${menuClass}` : ""}`}
          tabIndex={-1}
          style={fixed ? place! : undefined}
          role="menu"
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.stopPropagation()}
          onDragStart={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            const els = [...(wrap.current?.querySelectorAll<HTMLElement>(ITEMS) ?? [])];
            const i = els.indexOf(document.activeElement as HTMLElement);
            if (e.key === "ArrowDown") els[Math.min(els.length - 1, i + 1)]?.focus();
            else if (e.key === "ArrowUp") els[Math.max(0, i - 1)]?.focus();
            else if (e.key === "Escape") closeAndFocus();
            else {
              // Enter, Space, letters …: the item or the browser handles them; the row must not see them.
              if (!((e.metaKey || e.ctrlKey) && e.key.length === 1)) e.stopPropagation();
              return;
            }
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {children(closeAndFocus)}
        </div>
      )}
    </div>
  );
}
